import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiRefreshCw } from 'react-icons/fi';
import { listClaims, listPendingApprovals, getSlaReport, isExpenseApprover, isFinanceManager, getUserDisplayName } from '../../utils/expenseApi';

const STATUS_IN_FLIGHT = ['SUBMITTED'];

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

function StatusBadge({ status }) {
  return <span className={`exp-badge ${status}`}>{status.replace(/_/g, ' ')}</span>;
}

export default function ExpenseDashboard({ onNavigate }) {
  const [claims,    setClaims]    = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [sla,       setSla]       = useState(null);
  const [loading,   setLoading]   = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [claimsData] = await Promise.all([listClaims({ page_size: 100 })]);
      setClaims(claimsData.items || []);

      if (isExpenseApprover()) {
        const approvalData = await listPendingApprovals();
        setApprovals(Array.isArray(approvalData) ? approvalData : []);
      }
      if (isFinanceManager()) {
        const slaData = await getSlaReport();
        setSla(slaData);
      }
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const total       = claims.length;
  const totalSpend  = claims.reduce((s, c) => s + parseFloat(c.total_amount || 0), 0);
  const inFlight    = claims.filter(c => STATUS_IN_FLIGHT.includes(c.status)).length;
  const awaitingMe  = claims.filter(c => c.status === 'PENDING_APPROVAL').length;
  const recentClaims = [...claims].sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at)).slice(0, 5);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  if (loading) return (
    <div className="exp-spinner-wrap">
      <div className="exp-spinner" />
    </div>
  );

  return (
    <motion.div className="exp-section" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 className="exp-section-title" style={{ margin: 0 }}>
            Welcome, {getUserDisplayName().split(' ')[0] || 'User'}
          </h2>
          <p className="exp-section-subtitle" style={{ margin: '4px 0 0' }}>
            Here's your expense overview
          </p>
        </div>
        <motion.button className="exp-btn ghost small" onClick={load} whileTap={{ scale: 0.95 }}>
          <FiRefreshCw size={13} /> Refresh
        </motion.button>
      </div>

      {/* ── My Claims Stats ── */}
      <div className="exp-stats">
        <div className="exp-stat-card">
          <div className="exp-stat-label">My Claims</div>
          <div className="exp-stat-value">{total}</div>
          <div className="exp-stat-sub">all time</div>
        </div>
        <div className={`exp-stat-card ${totalSpend > 0 ? '' : ''}`}>
          <div className="exp-stat-label">Total Spend</div>
          <div className="exp-stat-value" style={{ fontSize: '1.4rem' }}>{fmt(totalSpend)}</div>
          <div className="exp-stat-sub">across all claims</div>
        </div>
        {inFlight > 0 && (
          <div className="exp-stat-card warn">
            <div className="exp-stat-label">In Progress</div>
            <div className="exp-stat-value">{inFlight}</div>
            <div className="exp-stat-sub">processing now</div>
          </div>
        )}
        {awaitingMe > 0 && (
          <div className="exp-stat-card warn">
            <div className="exp-stat-label">Awaiting Approval</div>
            <div className="exp-stat-value">{awaitingMe}</div>
            <div className="exp-stat-sub">your submitted claims</div>
          </div>
        )}

        {/* Approver stats */}
        {isExpenseApprover() && (
          <div className={`exp-stat-card ${approvals.length > 0 ? 'warn' : 'ok'}`}>
            <div className="exp-stat-label">Pending Approvals</div>
            <div className="exp-stat-value">{approvals.length}</div>
            <div className="exp-stat-sub">
              {approvals.filter(a => a.sla_breached).length > 0
                ? `${approvals.filter(a => a.sla_breached).length} SLA breached`
                : 'all within SLA'}
            </div>
          </div>
        )}

        {/* Finance manager SLA stats */}
        {sla && (
          <div className={`exp-stat-card ${sla.sla_breach_rate_pct > 0 ? 'err' : 'ok'}`}>
            <div className="exp-stat-label">SLA Breach Rate</div>
            <div className="exp-stat-value">{sla.sla_breach_rate_pct?.toFixed(1)}%</div>
            <div className="exp-stat-sub">{sla.breached_sla_count} breached of {sla.pending_approvals} pending</div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isExpenseApprover() ? '1fr 1fr' : '1fr', gap: 14 }}>
        {/* ── Recent Claims ── */}
        <div className="exp-card">
          <div className="exp-card-header">
            <span className="exp-card-title">Recent Claims</span>
            <motion.button className="exp-btn ghost xs" onClick={() => onNavigate('claims')} whileTap={{ scale: 0.95 }}>
              View all →
            </motion.button>
          </div>
          {recentClaims.length === 0 ? (
            <div className="exp-empty" style={{ padding: '24px' }}>
              <div className="exp-empty-msg">No claims yet</div>
              <motion.button
                className="exp-btn primary small"
                style={{ margin: '10px auto 0' }}
                onClick={() => onNavigate('submit')}
                whileTap={{ scale: 0.96 }}
              >
                Submit your first claim
              </motion.button>
            </div>
          ) : (
            <div className="exp-table-wrap" style={{ border: 'none', borderRadius: 0, boxShadow: 'none' }}>
              <table className="exp-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentClaims.map(c => (
                    <tr key={c.claim_id}>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.description || `Claim ${c.claim_id.slice(0, 8)}…`}
                      </td>
                      <td style={{ fontWeight: 700, color: 'var(--ep)' }}>
                        {parseFloat(c.total_amount).toLocaleString('en-IN')} {c.currency_code}
                      </td>
                      <td>{fmtDate(c.submitted_at)}</td>
                      <td><StatusBadge status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Pending Approvals (approvers only) ── */}
        {isExpenseApprover() && (
          <div className="exp-card">
            <div className="exp-card-header">
              <span className="exp-card-title">Pending Approvals</span>
              <motion.button className="exp-btn ghost xs" onClick={() => onNavigate('approvals')} whileTap={{ scale: 0.95 }}>
                Manage →
              </motion.button>
            </div>
            {approvals.length === 0 ? (
              <div className="exp-empty" style={{ padding: '24px' }}>
                <div className="exp-empty-msg">All caught up!</div>
                <div className="exp-empty-sub">No pending approvals.</div>
              </div>
            ) : (
              <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {approvals.slice(0, 5).map(a => (
                  <div
                    key={a.approval_id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 10px',
                      background: a.sla_breached ? '#fff8f8' : 'rgba(240,249,244,0.5)',
                      border: `1px solid ${a.sla_breached ? '#fca5a5' : 'var(--e-bdr)'}`,
                      borderRadius: 9,
                      fontSize: '0.84rem',
                      gap: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--e-txt)' }}>
                        {parseFloat(a.claim_total_amount).toLocaleString('en-IN')} {a.claim_currency}
                      </div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--e-muted)' }}>
                        Tier {a.approval_tier} · {fmtDate(a.sla_deadline)}
                      </div>
                    </div>
                    {a.sla_breached
                      ? <span className="exp-badge sla-breach">SLA Breached</span>
                      : <span className="exp-badge sla-ok">Within SLA</span>
                    }
                  </div>
                ))}
                {approvals.length > 5 && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--e-muted)', textAlign: 'center' }}>
                    +{approvals.length - 5} more
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Quick Actions ── */}
      <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <motion.button className="exp-btn primary" onClick={() => onNavigate('submit')} whileTap={{ scale: 0.96 }}>
          + New Claim
        </motion.button>
        <motion.button className="exp-btn" onClick={() => onNavigate('claims')} whileTap={{ scale: 0.96 }}>
          My Claims
        </motion.button>
        {isExpenseApprover() && (
          <motion.button className="exp-btn" onClick={() => onNavigate('approvals')} whileTap={{ scale: 0.96 }}>
            Approvals {approvals.length > 0 ? `(${approvals.length})` : ''}
          </motion.button>
        )}
        <motion.button className="exp-btn" onClick={() => onNavigate('query')} whileTap={{ scale: 0.96 }}>
          Ask AI
        </motion.button>
      </div>
    </motion.div>
  );
}
